import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { laundry_name: "", contact_name: "", location: "", branches: 1, phone: "", tier: "Not sure yet", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, branches: Number(form.branches) || 1 });
      toast.success("Thank you! OpsIQ will contact you within 24 hours.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again or reach us on WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3.5 text-base text-[#2C3E50] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#1ABC9C] focus:border-transparent";
  const labelCls = "block text-sm font-bold text-[#EBF5FB] mb-1.5";

  return (
    <section id="contact" data-testid="closing-cta-section" className="bg-[#0F2B3E] py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#1B4F72] bg-[#AED6F1] px-4 py-1.5 rounded-full mb-4">Get Started</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
            Contact OpsIQ today to get started
          </h2>
          <p className="mt-4 text-lg text-[#AED6F1] leading-relaxed">
            Tell us about your laundry business and we'll set up a free demo with your own services and prices — no commitment.
          </p>
          <div className="mt-8 space-y-4">
            <a href="tel:+233546080065" data-testid="contact-phone-link" className="flex items-center gap-4 text-white hover:text-[#1ABC9C] transition-colors">
              <span className="w-11 h-11 rounded-xl bg-[#1B4F72] flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-[#1ABC9C]" /></span>
              <span className="text-lg font-semibold">054 608 0065 (call or WhatsApp)</span>
            </a>
            <a href="mailto:alynttappiah@gmail.com" data-testid="contact-email-link" className="flex items-center gap-4 text-white hover:text-[#1ABC9C] transition-colors">
              <span className="w-11 h-11 rounded-xl bg-[#1B4F72] flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-[#1ABC9C]" /></span>
              <span className="text-lg font-semibold">alynttappiah@gmail.com</span>
            </a>
            <div className="flex items-center gap-4 text-white">
              <span className="w-11 h-11 rounded-xl bg-[#1B4F72] flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-[#1ABC9C]" /></span>
              <span className="text-lg font-semibold">Accra, Ghana</span>
            </div>
          </div>
        </div>
        <form data-testid="contact-form" onSubmit={submit} className="bg-[#1B4F72] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls} htmlFor="laundry-name">Laundry name</label>
              <input id="laundry-name" data-testid="contact-laundry-name-input" required className={inputCls} placeholder="e.g. FreshPress Laundry" value={form.laundry_name} onChange={set("laundry_name")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="contact-name">Your name</label>
              <input id="contact-name" data-testid="contact-name-input" required className={inputCls} placeholder="e.g. Kwame Mensah" value={form.contact_name} onChange={set("contact_name")} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls} htmlFor="location">Location in Ghana</label>
              <input id="location" data-testid="contact-location-input" required className={inputCls} placeholder="e.g. East Legon, Accra" value={form.location} onChange={set("location")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="branches">Number of branches</label>
              <input id="branches" data-testid="contact-branches-input" type="number" min="1" className={inputCls} value={form.branches} onChange={set("branches")} />
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="phone">Phone / WhatsApp</label>
            <input id="phone" data-testid="contact-phone-input" required className={inputCls} placeholder="e.g. 024 000 0000" value={form.phone} onChange={set("phone")} />
          </div>
          <div>
            <label className={labelCls} htmlFor="tier">Plan you're interested in</label>
            <select id="tier" data-testid="contact-tier-select" className={inputCls} value={form.tier} onChange={set("tier")}>
              <option>Not sure yet</option>
              <option>Basic</option>
              <option>Pro</option>
              <option>Premium</option>
              <option>Social Media Add-on</option>
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="message">Anything else? (optional)</label>
            <textarea id="message" data-testid="contact-message-input" rows="3" className={inputCls} placeholder="Tell us about your business..." value={form.message} onChange={set("message")} />
          </div>
          <button type="submit" data-testid="contact-submit-btn" disabled={sending} className="w-full inline-flex items-center justify-center gap-2 bg-[#1ABC9C] hover:bg-[#16A085] disabled:opacity-60 text-white text-lg font-bold px-8 py-4 rounded-full transition-colors">
            {sending ? "Sending..." : "Request my free demo"} <Send className="w-5 h-5" />
          </button>
        </form>
      </div>
    </section>
  );
}
